import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import {
  TextField,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Box,
  FormControl,
  Select,
  MenuItem,
  TableContainer,
} from "@mui/material";
import TimesheetEntryCard from "../../../components/ui/TimesheetEntryCard";
import { layout } from "../../../theme/tokens";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Tooltip from "@mui/material/Tooltip";
import { UserApi } from "../../../apis";
import { toast } from "react-toastify";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import WeekPicker from "../phases/weekPicker";
import dayjs from "dayjs";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { withHistoricalOptions, optionLabel } from "../../../utils/timesheetOptions";

// Create a SortableRow component that wraps both the main row and description row
function SortableTimeRow({ row, rowIndex, isEven, children, descriptionRow }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rowIndex });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <React.Fragment>
      <TableRow
        ref={setNodeRef}
        style={style}
        {...attributes}
        sx={{
          backgroundColor: isEven ? "background.subtle" : "background.paper",
          outline: "none !important",
          borderLeft: (t) => `1px solid ${t.palette.border.default}`,
          borderRight: (t) => `1px solid ${t.palette.border.default}`
        }}
      >
        {/* Drag handle */}
        <TableCell sx={{ cursor: 'grab', borderBottom: "none !important" }} {...listeners}>
          <DragIndicatorIcon />
        </TableCell>

        {/* Rest of row content */}
        {children}
      </TableRow>

      {/* Description row - part of the same draggable unit */}
      {descriptionRow}
    </React.Fragment>
  );
}

const AddTime = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [totalHours, setTotalHours] = useState("");
  const [timeEntries, setTimeEntries] = useState([]);
  const [note, setNote] = useState("");
  const [statusMessage, setStatusMessage] = useState(
    "Your time sheet status is pending."
  );
  const [statusChangeTrigger, setStatusChangeTrigger] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawProjects, setRawProjects] = useState([]);
  // What the dropdowns offer. The fetch returns only the projects still
  // assigned and active, so a week logged against a project that has since
  // been completed would render with an empty Project cell. Merging the
  // saved rows back in keeps the history readable — see the helper.
  const projects = withHistoricalOptions(rawProjects, timeEntries);
  const [timeSheetId, setTimeSheetId] = useState("");
  const [isSubmited, setIsSubmited] = useState(false);

  let userId = null;
  let userName = "";
  try {
    const userString = localStorage.getItem("user");
    const user = JSON.parse(userString);
    userId = user?.id;
    userName = user?.name;
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
  }

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await UserApi.getAssignedProjectsById(userId);
      const fetchedProjects = response.data.data.projects.map((proj) => ({
        projectName: proj.name,
        phases: proj.phases || [],
      }));

      setRawProjects(fetchedProjects);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setError("Failed to fetch projects.");
    } finally {
      setLoading(false);
    }
  };

  const getStartOfWeek = (date, useLegacyFormat = false) => {
    const currentDate = dayjs(date);
    const cutoffDate = dayjs('2025-10-12'); // October 12, 2025

    // If legacy format or date is before October 12, 2025, use Sunday as start
    if (useLegacyFormat || currentDate.isBefore(cutoffDate, 'day')) {
      // Calculate Sunday of the week (legacy format)
      const dayOfWeek = currentDate.day(); // 0 = Sunday, 1 = Monday, etc.
      return currentDate.subtract(dayOfWeek, 'day').startOf('day'); // Go back to Sunday
    }

    // Use Monday as start (new format) - this uses the global Day.js config
    return currentDate.startOf("week");
  };

  const getWeekDays = (selectedDate) => {
    // Check if the selected week is before October 12, 2025
    const cutoffDate = dayjs('2025-10-12');
    const isOldData = dayjs(selectedDate).isBefore(cutoffDate, 'day');
    const startOfWeek = getStartOfWeek(selectedDate, isOldData);

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const day = startOfWeek.add(i, "day");
      const dayFormatted = {
        day: day.format("ddd"),
        dateString: day.format("DD-MMM"),
      };
      weekDays.push(dayFormatted);
    }
    return weekDays;
  };
  // Defaults to today's position in the current week, so the phone opens on
  // the day the user is most likely filling in.
  const [selectedDayIndex, setSelectedDayIndex] = useState(() => {
    const today = dayjs().format("DD-MMM");
    const index = getWeekDays(dayjs()).findIndex((d) => d.dateString === today);
    return index >= 0 ? index : 0;
  });

  const [newRows, setNewRows] = useState([
    {
      projectName: "",
      phaseName: "",
      hours: getWeekDays(selectedDate).reduce(
        (acc, day) => ({ ...acc, [day.dateString]: 0 }), // Ensure hours are initialized to 0
        {}
      ),
      notes: "",
      description: "", // Added description field
    },
  ]);

  // New Monday-Sunday week (current)
  const getWeekDates = (selectedDate) => {
    const cutoffDate = dayjs('2025-10-12');
    const isOldData = dayjs(selectedDate).isBefore(cutoffDate, 'day');
    const start = getStartOfWeek(selectedDate, isOldData);
    const end = start.add(6, "day");
    return { startDate: start.format("DD-MM-YYYY"), endDate: end.format("DD-MM-YYYY") };
  };

  // Old Sunday-Saturday week (for backward compatibility)
  const getLegacyWeekDates = (selectedDate) => {
    const currentDate = dayjs(selectedDate);
    const dayOfWeek = currentDate.day(); // 0 = Sunday, 1 = Monday, etc.
    const start = currentDate.subtract(dayOfWeek, 'day').startOf('day'); // Go back to Sunday
    const end = start.add(6, "day");
    return { startDate: start.format("DD-MM-YYYY"), endDate: end.format("DD-MM-YYYY") };
  };

  useEffect(() => {
    if (selectedDate) {
      fetchProjects();
    }
  }, [selectedDate]);

  // Handle changes in hours
  const handleHourChange = (rowIndex, date, value, isNewRow = false) => {
    if (isNewRow) {
      const updatedNewRows = [...newRows];

      if (!updatedNewRows[rowIndex].hours) {
        updatedNewRows[rowIndex].hours = {};
      }

      updatedNewRows[rowIndex].hours[date] = parseFloat(value);

      setNewRows(updatedNewRows);
    } else {
      const updatedTimeEntries = [...timeEntries];

      if (!updatedTimeEntries[rowIndex].hours) {
        updatedTimeEntries[rowIndex].hours = {};
      }

      updatedTimeEntries[rowIndex].hours[date] = parseFloat(value);

      setTimeEntries(updatedTimeEntries);
    }
  };

  // Handle changes in description
  const handleDescriptionChange = (rowIndex, value, isNewRow = false) => {
    if (isNewRow) {
      const updatedNewRows = [...newRows];
      updatedNewRows[rowIndex].description = value;
      setNewRows(updatedNewRows);
    } else {
      const updatedTimeEntries = [...timeEntries];
      updatedTimeEntries[rowIndex].description = value;
      setTimeEntries(updatedTimeEntries);
    }
  };

  const handleNotesChange = (e) => {
    setNote(e.target.value);
  };

  // Get timesheet for selected week
  const getTimeSheetUser = async (selectedDate) => {
    if (!selectedDate) {
      console.error("Selected date is invalid.");
      return;
    }
    // set description to empty
    setNote("");
    setTotalHours(0);
    const { startDate, endDate } = getWeekDates(selectedDate);
    const parsedStartDate = parseDate(startDate);
    const parsedEndDate = parseDate(endDate);

    if (!parsedStartDate || !parsedEndDate) {
      console.error("Invalid date values detected.");
      setError("Invalid week start or end date.");
      return;
    }

    const formattedStartDate = formatDate(parsedStartDate);
    const formattedEndDate = formatDate(parsedEndDate);

    const submissionDetails = {
      userId: userId,
      weekStart: formattedStartDate,
      weekEnd: formattedEndDate,
    };

    if (!userId) {
      console.error("User ID is missing.");
      setError("User ID is required.");
      return;
    }

    try {
      const response = await UserApi.getTimeSheetByPayload(submissionDetails);
      const totalHours = response?.data?.totalHours || 0;
      const note = response?.data?.notes;
      const timesheetId = response?.data?.id;
      const status = response?.data?.status;
      const isSubmited = response?.data?.isSubmited;

      if (status === "approved") {
        setStatusMessage("Time sheet is approved.");
      } else if (status === "rejected") {
        setStatusMessage("Time sheet is rejected.");
      } else {
        setStatusMessage("Your time sheet status is pending.");
      }
      setIsSubmited(isSubmited);
      setTimeSheetId(timesheetId);
      setNote(note);

      let rows = response?.data?.rows || [];
      if (typeof rows === "string") {
        rows = JSON.parse(rows);
      }

      if (rows.length === 0) {
        setNewRows([
          {
            projectName: "",
            phaseName: "",
            hours: getWeekDays(selectedDate).reduce(
              (acc, day) => ({ ...acc, [day.dateString]: 0 }), // Ensure hours are initialized to 0
              {}
            ),
            notes: "",
            description: "", // Added description field
          },
        ]);
        setTimeEntries([]);
        setTotalHours(0);
        setError("No data available for the selected week.");
      } else {
        rows.forEach((row) => {
          const hours = {};
          getWeekDays(selectedDate).forEach((day) => {
            const dateString = day.dateString;
            hours[dateString] = row.hours[dateString] || 0;
          });
          row.hours = hours;
        });
        setTimeEntries(rows);
        setNewRows([]);
        setTotalHours(totalHours);
      }
    } catch (error) {
      setTotalHours(0);
      setNewRows([]);
      setNote("");
      setTimeEntries([]);
      console.error("Error fetching timesheet data:", error);
      setError("An error occurred while fetching the timesheet data.");
      setStatusMessage("Your time sheet status is pending.");
    }
  };

  useEffect(() => {
    if (selectedDate) {
      getTimeSheetUser(selectedDate);
    }
  }, [selectedDate]);

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const parseDate = (dateString) => {
    const dateParts = dateString.split("-");
    if (dateParts.length === 3) {
      const day = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1;
      const year = parseInt(dateParts[2], 10);

      return new Date(year, month, day);
    }
    return null;
  };

  const handleExportPDF = async () => {
    const input = document.getElementById("timesheet");

    const canvas = await html2canvas(input);
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");

    const padding = 10;

    const pageWidth = 210;
    const imgWidth = pageWidth - 2 * padding;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const imgPositionX = padding;
    const imgPositionY = 6;

    pdf.addImage(
      imgData,
      "PNG",
      imgPositionX,
      imgPositionY,
      imgWidth,
      imgHeight
    );
    pdf.save("timesheet.pdf");
  };

  useEffect(() => { }, [statusMessage, statusChangeTrigger]);

  const getPhasesForProject = (projectName) => {
    const project = projects.find(
      (project) => project.projectName === projectName
    );
    return project ? project.phases : [];
  };

  const calculateTotalHours = (newRows) => {
    let total = 0;
    newRows.forEach((entry) => {
      Object.values(entry?.hours).forEach((hours) => {
        total += Number(hours) || 0; // Convert to number, default to 0 if empty
      });
    });

    return total;
  };

  // When submitting for approval: require description for every row that has data (project/phase or hours)
  const rowNeedsDescriptionForUI = (row) => {
    if (!row) return false;
    const rowHours = Object.values(row?.hours || {}).reduce(
      (sum, h) => sum + (Number(h) || 0),
      0
    );
    const hasProject = !!(row?.projectName && String(row.projectName).trim());
    const hasPhase = !!(row?.phaseName && String(row.phaseName).trim());
    const rowHasData = rowHours > 0 || hasProject || hasPhase;
    if (!rowHasData) return false;
    const desc = (row.description != null ? String(row.description) : "").trim();
    return desc.length === 0;
  };

  // When submitting for approval: also block if any row has empty description (e.g. single empty row)
  const rowHasEmptyDescription = (row) => {
    if (!row) return false;
    const desc = (row.description != null ? String(row.description) : "").trim();
    return desc.length === 0;
  };

  const allRowsForSubmit = [...timeEntries, ...newRows];
  const hasRowsNeedingDescription = allRowsForSubmit.some(rowNeedsDescriptionForUI);
  const hasAnyRowWithEmptyDescription = allRowsForSubmit.some(rowHasEmptyDescription);
  const isSubmitDisabled = hasRowsNeedingDescription || hasAnyRowWithEmptyDescription;

  const handleSubmit = async (isSubmited = true) => {
    const { startDate, endDate } = getWeekDates(selectedDate);
    let rows;
    rows = newRows.map((entry) => {
      return {
        projectName: entry?.projectName,
        phaseName: entry?.phaseName,
        hours: entry.hours,
        notes: note || "",
        description: entry.description || "",
        isSubmited: isSubmited,
      };
    });
    const rowsToSend = timeSheetId ? [...timeEntries, ...(rows || [])] : (rows || []);
    const anyRowMissingDescription = rowsToSend.length > 0 && rowsToSend.some(rowHasEmptyDescription);
    if (isSubmited && (rowsToSend.some(rowNeedsDescriptionForUI) || anyRowMissingDescription)) {
      toast.error("Add a description of the work required for each row.");
      return;
    }
    try {
      await getTimeSheetUser(selectedDate);

      if (timeSheetId) {
        const existingRows = timeEntries;
        const mergedRows = [...existingRows, ...rows];
        const totalHours = calculateTotalHours(mergedRows);
        console.log("Total hours:", totalHours);
        if (isSubmited) {
          mergedRows.forEach((entry) => {
            entry.isSubmited = isSubmited;
          });
        }
        await UserApi.updateTimeSheet({
          id: timeSheetId,
          userId: userId,
          rows: mergedRows,
          notes: note,
          weekStart: startDate,
          weekEnd: endDate,
          totalHours: totalHours,
          isSubmited: isSubmited,
        });

        setTimeEntries(mergedRows);
        setTotalHours(totalHours);
        setNote(note);
        setTimeSheetId(timeSheetId);
        getTimeSheetUser(selectedDate);
      } else {
        const totalHours = calculateTotalHours(rows);
        const createResponse = await UserApi.createTimeSheet({
          userId: userId,
          rows: rows,
          notes: note,
          weekStart: startDate,
          weekEnd: endDate,
          totalHours: totalHours,
          isSubmited: isSubmited,
        });

        setTimeEntries(rows);
        setTotalHours(totalHours);
        setNote(note);
        setTimeSheetId(createResponse.data.timeSheetId);
        setStatusMessage("Your new timesheet has been created.");
        getTimeSheetUser(selectedDate);
      }
    } catch (error) {
      console.error("Error handling timesheet submission:", error);
      setError("An error occurred while handling your timesheet.");
    }
  };

  const handleAddRow = () => {
    setNewRows((prevRows) => [
      ...prevRows,
      {
        projectName: "",
        phaseName: "",
        hours: getWeekDays(selectedDate).reduce(
          (acc, day) => ({ ...acc, [day.dateString]: 0 }),
          {}
        ),
        notes: "",
        description: "", // Added description field
      },
    ]);
  };

  // Rows could be added but never removed, so a mistyped line stayed on the
  // timesheet forever. Unsaved rows drop immediately; a saved row is removed
  // from local state and the deletion persists on the next save, which is how
  // every other edit on this screen already behaves.
  const handleDeleteRow = (rowIndex, isNewRow = false) => {
    const row = isNewRow ? newRows[rowIndex] : timeEntries[rowIndex];

    if (row?.isSubmited) {
      toast.info("Submitted rows can't be removed.");
      return;
    }

    const hasContent =
      row?.projectName ||
      row?.phaseName ||
      row?.description ||
      Object.values(row?.hours || {}).some((value) => parseFloat(value) > 0);

    if (hasContent && !window.confirm("Remove this row from the timesheet?")) {
      return;
    }

    if (isNewRow) {
      setNewRows((prev) => prev.filter((_, i) => i !== rowIndex));
    } else {
      setTimeEntries((prev) => prev.filter((_, i) => i !== rowIndex));
    }
  };

  // Mobile shows one day at a time: a week of seven hour boxes per entry was
  // the main thing making this screen feel complicated on a phone. The data
  // is still weekly — this only narrows what is on screen and what gets
  // edited, so saving still writes the whole week.
  const weekDays = getWeekDays(selectedDate);
  const selectedDay = weekDays[selectedDayIndex] || weekDays[0];

  const dayTotal = [...timeEntries, ...newRows].reduce(
    (sum, entry) =>
      sum + (parseFloat(entry?.hours?.[selectedDay?.dateString]) || 0),
    0
  );

  // Stepping past either end of the week rolls into the neighbouring one, so
  // the day arrows alone are enough to move around without the week picker.
  const goToDay = (delta) => {
    const next = selectedDayIndex + delta;
    if (next < 0) {
      handleWeekChange("past");
      setSelectedDayIndex(6);
    } else if (next > 6) {
      handleWeekChange("future");
      setSelectedDayIndex(0);
    } else {
      setSelectedDayIndex(next);
    }
  };

  const handleWeekChange = (direction) => {
    let newDate = dayjs(selectedDate);

    if (direction === "past") {
      newDate = newDate.subtract(7, "day");
    } else if (direction === "future") {
      newDate = newDate.add(7, "day");
    }

    setSelectedDate(newDate);
  };

  const handleSaveAsDraft = async () => {
    // call handleSubmit function with isSubmited set to false
    handleSubmit(false);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setTimeEntries((items) => {
        const oldIndex = active.id;
        const newIndex = over.id;
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 1.5, md: 2.5 },
        m: { xs: 0, md: "20px auto" },
        overflowX: "hidden",
        mb: { xs: 12, md: 0 },
      }}
    >
      <div id="timesheet">
        <Typography
          variant="h5"
          align="center"
          sx={{ fontWeight: 600, display: { xs: "none", md: "block" } }}
        >
          Add Weekly Time Sheet
        </Typography>
        <Typography
          variant="h5"
          align="center"
          sx={{ fontWeight: 600, display: { xs: "block", md: "none" } }}
        >
          Timesheet
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          {userName}
        </Typography>

        <Grid mt={2} container rowSpacing={2} columnSpacing={{ xs: 0, md: 2 }}>
          {/* Phones step through one day at a time; the week picker stays on
              desktop, where the whole week is on screen anyway. */}
          <Grid item xs={12} sx={{ display: { xs: "block", md: "none" } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => goToDay(-1)}
                aria-label="Previous day"
                sx={{ minWidth: 44, px: 1.5 }}
              >
                ‹
              </Button>
              <Box sx={{ textAlign: "center", minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {selectedDay?.day}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedDay?.dateString}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => goToDay(1)}
                aria-label="Next day"
                sx={{ minWidth: 44, px: 1.5 }}
              >
                ›
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ display: { xs: "none", md: "block" } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => handleWeekChange("past")}
                aria-label="Previous week"
                sx={{ minWidth: 44, px: 1.5 }}
              >
                ‹
              </Button>
              <WeekPicker
                selectedDate={selectedDate}
                mb={0}
                onDateChange={(date) => setSelectedDate(date)}
              />
              <Button
                variant="outlined"
                onClick={() => handleWeekChange("future")}
                aria-label="Next week"
                sx={{ minWidth: 44, px: 1.5 }}
              >
                ›
              </Button>
            </Box>
          </Grid>

          <Grid mt={3} item xs={12}>
            {/* Phones: one card per entry. The table below takes over from md. */}
            <Box sx={{ display: { xs: "block", md: "none" } }}>
              {timeEntries.map((row, rowIndex) => {
                const selectedProject = projects.find(
                  (project) => project.projectName === row.projectName
                );

                return (
                  <TimesheetEntryCard
                    key={`m-${rowIndex}`}
                    row={row}
                    weekDays={selectedDay ? [selectedDay] : []}
                    projects={projects}
                    phases={selectedProject ? selectedProject.phases : []}
                    disabled={row?.isSubmited}
                    index={rowIndex}
                    onProjectChange={(projectName) => {
                      setTimeEntries((prev) =>
                        prev.map((entry, i) =>
                          i === rowIndex
                            ? { ...entry, projectName, phaseName: "" }
                            : entry
                        )
                      );
                    }}
                    onPhaseChange={(phaseName) => {
                      setTimeEntries((prev) =>
                        prev.map((entry, i) =>
                          i === rowIndex ? { ...entry, phaseName } : entry
                        )
                      );
                    }}
                    onHourChange={(dateString, value) =>
                      handleHourChange(rowIndex, dateString, value)
                    }
                    onDescriptionChange={(text) =>
                      handleDescriptionChange(rowIndex, text)
                    }
                    onRemove={() => handleDeleteRow(rowIndex)}
                    collapsible
                  />
                );
              })}

              {newRows.map((row, rowIndex) => (
                <TimesheetEntryCard
                  key={`mn-${rowIndex}`}
                  row={row}
                  weekDays={selectedDay ? [selectedDay] : []}
                  projects={projects}
                  phases={getPhasesForProject(row.projectName) || []}
                  disabled={false}
                  index={rowIndex}
                  onProjectChange={(selectedProjectName) => {
                    setNewRows((prevRows) =>
                      prevRows.map((newRow, i) =>
                        i === rowIndex
                          ? {
                            ...newRow,
                            projectName: selectedProjectName,
                            phaseName: "",
                            hours: getWeekDays(selectedDate).reduce(
                              (acc, day) => {
                                acc[day.dateString] = "";
                                return acc;
                              },
                              {}
                            ),
                          }
                          : newRow
                      )
                    );
                  }}
                  onPhaseChange={(selectedPhase) => {
                    setNewRows((prevRows) =>
                      prevRows.map((newRow, i) =>
                        i === rowIndex
                          ? { ...newRow, phaseName: selectedPhase }
                          : newRow
                      )
                    );
                  }}
                  onHourChange={(dateString, value) =>
                    handleHourChange(rowIndex, dateString, value, true)
                  }
                  onDescriptionChange={(text) =>
                    handleDescriptionChange(rowIndex, text, true)
                  }
                  onRemove={() => handleDeleteRow(rowIndex, true)}
                  collapsible
                />
              ))}
            </Box>

            <TableContainer
              sx={{ display: { xs: "none", md: "block" }, overflowX: "auto" }}
            >
            <Table
              size="small"
              sx={{
                "& .MuiTableCell-root": { px: 1, py: 0.75 },
                "& .MuiTableCell-head": { px: 1 },
              }}
            >
              <TableHead>
                <TableRow >
                  {timeEntries.length > 0 && <TableCell sx={{ width: 50 }}></TableCell>} {/* For drag handle - only show when there are entries */}
                  <TableCell>Project</TableCell>
                  <TableCell>Phase</TableCell>
                  {getWeekDays(selectedDate).map((day) => (
                    <TableCell key={day.dateString}>
                      {day.day} <br />
                      {day.dateString}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ width: 56 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={timeEntries.map((_, i) => i)}
                    strategy={verticalListSortingStrategy}
                  >
                    {timeEntries.map((row, rowIndex) => {
                      const isEven = rowIndex % 2 === 0;
                      const selectedProject = projects.find(
                        (project) => project.projectName === row.projectName
                      );
                      const filteredPhases = selectedProject
                        ? selectedProject.phases
                        : [];

                      return (
                        <SortableTimeRow
                          key={rowIndex}
                          row={row}
                          rowIndex={rowIndex}
                          isEven={isEven}
                          descriptionRow={
                            <TableRow sx={{
                              backgroundColor: isEven ? "background.subtle" : "background.paper",
                              outline: "none !important",
                              borderLeft: (t) => `1px solid ${t.palette.border.default}`,
                              borderRight: (t) => `1px solid ${t.palette.border.default}`
                            }}>
                              <TableCell colSpan={timeEntries.length > 0 ? 11 : 10}>
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      mb: 0.5,
                                      color: "text.secondary",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Description of the work required
                                  </Typography>
                                  <TextField
                                    placeholder="Describe the work required for this row (required)"
                                    name="description"
                                    fullWidth
                                    multiline
                                    minRows={1}
                                    disabled={row?.isSubmited}
                                    value={row.description || ""}
                                    onChange={(event) =>
                                      handleDescriptionChange(
                                        rowIndex,
                                        event.target.value
                                      )
                                    }
                                  />
                                </Box>
                              </TableCell>
                            </TableRow>
                          }
                        >
                          <TableCell sx={{ borderBottom: "none !important" }}>
                            <FormControl fullWidth>
                              <Select
                                sx={{ minWidth: 150 }}
                                value={row.projectName || ""}
                                disabled={row?.isSubmited}
                                onChange={(event) => {
                                  const projectName = event.target.value;
                                  const selectedProject = projects.find(
                                    (project) => project.projectName === projectName
                                  );
                                  if (selectedProject) {
                                    setTimeEntries((prev) =>
                                      prev.map((entry, i) =>
                                        i === rowIndex
                                          ? {
                                            ...entry,
                                            projectName,
                                            phaseName: "",
                                          }
                                          : entry
                                      )
                                    );
                                  }
                                }}
                              >
                                <MenuItem value="" disabled>
                                  Select Project
                                </MenuItem>
                                {projects
                                  .slice()
                                  .sort((a, b) => a.projectName.localeCompare(b.projectName))
                                  .map((project) => (
                                    <MenuItem
                                      key={project.projectName}
                                      value={project.projectName}
                                    >
                                      {optionLabel(project.projectName, project.unavailable)}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          </TableCell>

                          <TableCell sx={{ borderBottom: "none !important" }}>
                            <FormControl fullWidth>
                              <Select
                                sx={{
                                  minWidth: 150,
                                  pointerEvents: !row?.isSubmited ? "auto" : "none",
                                  "& .MuiSelect-select": {
                                    color: row.phaseName ? "inherit" : "grey",
                                  },
                                }}
                                disabled={row?.isSubmited}
                                value={row.phaseName || ""}
                                onChange={(event) => {
                                  const selectedPhase = event.target.value;
                                  setTimeEntries((prev) =>
                                    prev.map((entry, i) =>
                                      i === rowIndex
                                        ? {
                                          ...entry,
                                          phaseName: selectedPhase,
                                        }
                                        : entry
                                    )
                                  );
                                }}
                              >
                                <MenuItem value="" disabled={row?.isSubmited}>
                                  Select Phase
                                </MenuItem>
                                {filteredPhases.length > 0 ? (
                                  filteredPhases.map((filteredPhase) => (
                                    <MenuItem
                                      key={filteredPhase.id}
                                      value={filteredPhase.name}
                                    >
                                      {filteredPhase.name}
                                    </MenuItem>
                                  ))
                                ) : (
                                  <MenuItem disabled>No phases available</MenuItem>
                                )}
                              </Select>
                            </FormControl>
                          </TableCell>

                          {getWeekDays(selectedDate).map((day) => (
                            <TableCell key={day.dateString} sx={{ borderBottom: "none !important" }}>
                              <TextField
                                type="number"
                                inputProps={{
                                  min: 0,
                                  step: 0.5,
                                  readOnly: row?.isSubmited,
                                }}
                                fullWidth
                                disabled={row?.isSubmited}
                                value={row.hours[day.dateString] || ""}
                                sx={{
                                  minWidth: 56,
                                  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                                    { appearance: "none", margin: 0 },
                                  "& input[type=number]": {
                                    MozAppearance: "textfield",
                                  },
                                }}
                                onChange={(event) =>
                                  handleHourChange(rowIndex, day.dateString, event.target.value)
                                }
                              />
                            </TableCell>
                          ))}
                          <TableCell align="right" sx={{ borderBottom: "none !important" }}>
                            <Tooltip title={Boolean(row?.isSubmited) ? "Submitted rows can't be removed" : "Remove row"}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={Boolean(row?.isSubmited)}
                                  onClick={() => handleDeleteRow(rowIndex)}
                                  aria-label="Remove row"
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </SortableTimeRow>
                      );
                    })}
                  </SortableContext>
                </DndContext>

                {newRows.map((row, rowIndex) => {
                  const isOdd = rowIndex % 2 !== 0;
                  return (
                    <React.Fragment key={`new-row-${rowIndex}`}>
                      <TableRow key={`new-${rowIndex}`} sx={{ backgroundColor: isOdd ? "background.subtle" : "background.paper", outline: "none !important", borderLeft: (t) => `1px solid ${t.palette.border.default}`, borderRight: (t) => `1px solid ${t.palette.border.default}` }}>
                        {timeEntries.length > 0 && <TableCell sx={{ borderBottom: "none !important" }}></TableCell>} {/* Empty cell for alignment when drag handle is present */}
                        <TableCell sx={{ borderBottom: "none !important" }}>
                          <FormControl fullWidth>
                            <Select
                              sx={{ minWidth: 150 }}
                              value={row.projectName || ""}
                              onChange={(event) => {
                                const selectedProjectName = event.target.value;
                                setNewRows((prevRows) =>
                                  prevRows.map((newRow, i) =>
                                    i === rowIndex
                                      ? {
                                        ...newRow,
                                        projectName: selectedProjectName,
                                        phaseName: "",
                                        hours: getWeekDays(selectedDate).reduce(
                                          (acc, day) => {
                                            acc[day.dateString] = ""; // Initialize empty hours for each date
                                            return acc;
                                          },
                                          {}
                                        ),
                                      }
                                      : newRow
                                  )
                                );
                              }}
                            >
                              <MenuItem value="" disabled>
                                Select Project
                              </MenuItem>
                              {/* {projects.map((project) => (
                              <MenuItem
                                key={project.projectName}
                                value={project.projectName}
                              >
                                {project.projectName}
                              </MenuItem>
                            ))} */}
                              {projects
                                .slice() // Create a shallow copy to avoid mutating the original array
                                .sort((a, b) => a.projectName.localeCompare(b.projectName)) // Sort alphabetically
                                .map((project) => (
                                  <MenuItem key={project.projectName} value={project.projectName}>
                                    {optionLabel(project.projectName, project.unavailable)}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </TableCell>

                        <TableCell sx={{ borderBottom: "none !important" }}>
                          <FormControl fullWidth>
                            <Select
                              sx={{
                                minWidth: 150,
                                "& .MuiSelect-select": {
                                  color: row.phaseName ? "inherit" : "grey",
                                },
                              }}
                              value={row.phaseName || ""}
                              onChange={(event) => {
                                const selectedPhase = event.target.value;
                                setNewRows((prevRows) =>
                                  prevRows.map((newRow, i) =>
                                    i === rowIndex
                                      ? { ...newRow, phaseName: selectedPhase }
                                      : newRow
                                  )
                                );
                              }}
                            >
                              <MenuItem value="" disabled>
                                Select Phase
                              </MenuItem>
                              {/* {getPhasesForProject(row.projectName)?.length > 0 ? (
                              getPhasesForProject(row.projectName).map(
                                (phase, index) => (
                                  <MenuItem key={index} value={phase.name}>
                                    {phase.name}
                                  </MenuItem>
                                )
                              )
                            ) : (
                              <MenuItem disabled>No phases available</MenuItem>
                            )} */}
                              {getPhasesForProject(row.projectName)?.length > 0 ? (
                                getPhasesForProject(row.projectName)
                                  .slice() // Create a shallow copy to avoid modifying the original array
                                  .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically
                                  .map((phase, index) => (
                                    <MenuItem key={index} value={phase.name}>
                                      {phase.name}
                                    </MenuItem>
                                  ))
                              ) : (
                                <MenuItem disabled>No phases available</MenuItem>
                              )}

                            </Select>
                          </FormControl>
                        </TableCell>

                        {getWeekDays(selectedDate).map((day) => (
                          <TableCell key={day.dateString} sx={{ borderBottom: "none !important" }}>
                            <TextField
                              type="number"
                              inputProps={{
                                min: 0,
                                step: 0.5,
                              }}
                              fullWidth
                              value={row.hours[day.dateString] || ""}
                              onChange={(event) =>
                                handleHourChange(
                                  rowIndex,
                                  day.dateString,
                                  event.target.value,
                                  true
                                )
                              }
                              sx={{
                                minWidth: 56,
                                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                                {
                                  appearance: "none",
                                  margin: 0,
                                },
                                "& input[type=number]": {
                                  MozAppearance: "textfield",
                                },
                              }}
                            />
                          </TableCell>
                        ))}
                          <TableCell align="right" sx={{ borderBottom: "none !important" }}>
                            <Tooltip title={false ? "Submitted rows can't be removed" : "Remove row"}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={false}
                                  onClick={() => handleDeleteRow(rowIndex, true)}
                                  aria-label="Remove row"
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                      </TableRow>
                      <TableRow key={`new-${rowIndex}-description`} sx={{ backgroundColor: isOdd ? "background.subtle" : "background.paper", borderLeft: (t) => `1px solid ${t.palette.border.default}`, borderRight: (t) => `1px solid ${t.palette.border.default}` }}>
                        <TableCell colSpan={timeEntries.length > 0 ? 11 : 10}>
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mb: 0.5,
                                color: "text.secondary",
                                fontWeight: 600,
                              }}
                            >
                              Description of the work required
                            </Typography>
                            <TextField
                              placeholder="Describe the work required for this row (required)"
                              name="description"
                              fullWidth
                              multiline
                              minRows={1}
                              value={row.description || ""}
                              onChange={(event) =>
                                handleDescriptionChange(
                                  rowIndex,
                                  event.target.value,
                                  true
                                )
                              }
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            </TableContainer>

            <Box
              sx={{
                mt: 2,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddRow}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Add Row
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ display: { xs: "none", md: "block" } }}>
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  boxSizing: "border-box",
                  py: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: { sm: "flex-end" },
                    gap: 1,
                    "& > button": { width: { xs: "100%", sm: "auto" } },
                  }}
                >
                  <Button
                    onClick={() => {
                      handleSaveAsDraft();
                    }}
                    variant="outlined"
                  >
                    Save as Draft
                  </Button>
                  <Button
                    onClick={() => handleSubmit(true)}
                    disabled={isSubmitDisabled}
                    variant="contained"
                    color="primary"
                    title={
                      isSubmitDisabled
                        ? "Add a description of work for each row (project, phase, or hours entered)"
                        : ""
                    }
                  >
                    Submit Time Sheet For Approval
                  </Button>

                  <Button onClick={handleExportPDF} variant="outlined">
                    Export As PDF
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 2,
            py: 1.5,
            display: { xs: "none", md: "flex" },
            justifyContent: "center",
            alignItems: "baseline",
            gap: 1,
            borderTop: 1,
            borderColor: "border.subtle",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Total hours this week
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {totalHours || 0}
          </Typography>
        </Box>

        {/* On a phone the actions sat below every entry — several screens down
            on a full week. Pin them, above the bottom tab bar, so the running
            total and Submit are always in reach.
            Portalled to <body>: a position:fixed element is positioned against
            the nearest ancestor that has a transform/filter/animation rather
            than the viewport, and this sits inside both the page-transition
            wrapper and the timesheet card. */}
        {createPortal(
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            position: "fixed",
            left: 0,
            right: 0,
            bottom: `calc(${layout.bottomNavHeight}px + env(safe-area-inset-bottom))`,
            zIndex: (t) => t.zIndex.appBar - 1,
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.25,
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "border.default",
            boxShadow: (t) => t.shadows[8],
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1 }}>
              {selectedDay?.day || "Total"}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {dayTotal} h
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Button size="small" variant="outlined" onClick={handleSaveAsDraft}>
            Save
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => handleSubmit(true)}
            disabled={isSubmitDisabled}
            title={
              isSubmitDisabled
                ? "Add a description of work for each row (project, phase, or hours entered)"
                : ""
            }
          >
            Submit
          </Button>
        </Box>,
        document.body
        )}
      </div>
    </Paper>
  );
};

export default AddTime;